<template>
    <StepShell :embedded="embedded" layout="page" gap="none">
        <template v-if="generationFailed">
            <StepHeader
                v-if="embedded"
                embedded
                :title="t('onboarding.calendarFirst.profilePublishStep.failed.title')"
                :lede="t('onboarding.calendarFirst.profilePublishStep.failed.lede')" />
            <BasePageHeader
                v-else
                :title="t('onboarding.calendarFirst.profilePublishStep.failed.title')"
                :subtitle="t('onboarding.calendarFirst.profilePublishStep.failed.lede')"
                no-border
                subtitle-below />

            <StepActions>
                <BaseButton theme="primary" :disabled="busy" @click="emit('skip')">{{
                    embedded ? t("onboarding.calendarFirst.profilePublishStep.continueWithout") : t("onboarding.calendarFirst.profilePublishStep.failed.finish")
                }}</BaseButton>
            </StepActions>
        </template>

        <template v-else>
            <StepHeader v-if="embedded" embedded class="mb-6" :title="t('onboarding.calendarFirst.profilePublishStep.title')" :lede="introCopy" source="profile" />
            <BasePageHeader v-else :title="t('onboarding.calendarFirst.profilePublishStep.heading')" :subtitle="introCopy" no-border subtitle-below>
                <template #eyebrow
                    ><p class="eyebrow">{{ t("onboarding.calendarFirst.profilePublishStep.eyebrow") }}</p></template
                >
            </BasePageHeader>

            <!-- Generation overwrites existing How to Work With Me content before this screen renders, so
                 disclose the replacement to users who had content.
                 -->
            <p v-if="preview.had_existing_content" class="rewritten">
                <i aria-hidden="true" class="bi bi-info-circle"></i>
                <span>{{ t("onboarding.calendarFirst.profilePublishStep.rewritten") }}</span>
            </p>

            <section class="document">
                <!-- The collaboration profile page only becomes editable after publishing writes the public-profile row, so this preview is the only place to edit before deciding who sees it. -->
                <ProfileTopicSection
                    :is-loading="false"
                    :error="null"
                    has-profile
                    :entries="entries"
                    :empty-profile-state="emptyState"
                    :empty-entries-state="emptyState"
                    :editable="!busy"
                    @editing="isEditing = $event"
                    @saved="(id, content) => emit('saved', id, content)" />
            </section>

            <!-- Publishing would return 403 when private is the only allowed access level, so omit the
                 publish action.
                 -->
            <div aria-live="polite">
                <p v-if="isEditing" class="choice-copy">{{ t("onboarding.calendarFirst.profilePublishStep.choice.editing") }}</p>

                <p v-else-if="isEmpty" class="choice-copy">{{ t("onboarding.calendarFirst.profilePublishStep.choice.empty") }}</p>

                <p v-else-if="privateOnly" class="private-only">{{ t("onboarding.calendarFirst.profilePublishStep.choice.privateOnly") }}</p>

                <p v-else class="choice-copy">
                    <span v-if="profileSharerNames.length > 0">{{ t("onboarding.calendarFirst.profilePublishStep.choice.sharers", { names: sharerFirstNames }) + " " }}</span
                    >{{ publishBody }}
                    {{ t("onboarding.calendarFirst.profilePublishStep.choice.changeAnytime") }}
                </p>
            </div>

            <!-- Separate action buttons prevent a selected privacy option from contradicting the submit
                 button's label.
                 -->
            <StepActions v-if="isEmpty">
                <BaseButton theme="primary" :disabled="cannotContinue" @click="handleSkip">{{ t("onboarding.calendarFirst.profilePublishStep.continueWithout") }}</BaseButton>
            </StepActions>
            <StepActions v-else>
                <template #start>
                    <BaseButton theme="neutral" :disabled="cannotContinue" @click="handlePublish('private')">{{
                        t("onboarding.calendarFirst.profilePublishStep.keepPrivate")
                    }}</BaseButton>
                </template>
                <BaseButton v-if="publishAccess" theme="primary" :disabled="cannotContinue" @click="handlePublish(publishAccess)">{{
                    t("onboarding.calendarFirst.profilePublishStep.publish")
                }}</BaseButton>
            </StepActions>
        </template>
    </StepShell>
</template>

<script setup lang="ts">
import StepHeader from "~vue/CalendarFirstOnboarding/StepHeader.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import ProfileTopicSection from "~vue/components/ProfileTopicSection.vue";
import { toProfileEntry } from "~vue/utils/profileEntries";
import { firstNameOf } from "~vue/utils/stringUtils";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import StepActions from "./StepActions.vue";
import StepShell from "./StepShell.vue";
import type { AccessLevel, ProfilePreview } from "./types";

const {
    embedded = false,
    preview,
    availableAccessLevels,
    profileSharerNames,
    generationFailed,
    busy,
} = defineProps<{
    embedded?: boolean;
    preview: ProfilePreview;
    availableAccessLevels: AccessLevel[];
    profileSharerNames: string[];
    generationFailed: boolean;
    busy: boolean;
}>();

const emit = defineEmits<{ publish: [AccessLevel]; skip: []; saved: [id: string, content: string] }>();
const { t } = useI18n();
const isEditing = ref(false);
const cannotContinue = computed(() => busy || isEditing.value);

function handlePublish(access: AccessLevel): void {
    if (!cannotContinue.value && !isEmpty.value) {
        emit("publish", access);
    }
}

function handleSkip(): void {
    if (!cannotContinue.value) {
        emit("skip");
    }
}

const MAX_NAMED_SHARERS = 3;

/*
 * Generation has already created a profile here. Both renderer empty states use this message because
 * skipped questions can leave that profile without content.
 */
const emptyState = computed(() => ({
    title: t("onboarding.calendarFirst.profilePublishStep.emptyState.title"),
    body: t("onboarding.calendarFirst.profilePublishStep.emptyState.body"),
    icon: "bi-pencil",
}));

const entries = computed(() =>
    preview.subtopics.map((subtopic) => toProfileEntry({ id: subtopic.id, title: subtopic.title, content: subtopic.content, placeholder: subtopic.placeholder })),
);

/*
 * or the choice offered here 403s on submit. Org-wide is preferred when it is
 * available; otherwise the narrower invited-members level is the real publish.
 */
const publishAccess = computed<AccessLevel | null>(() => {
    if (availableAccessLevels.includes("organization")) {
        return "organization";
    }
    if (availableAccessLevels.includes("invited_org_members")) {
        return "invited_org_members";
    }
    return null;
});

const privateOnly = computed(() => publishAccess.value === null);

const writtenCount = computed(() => preview.subtopics.filter((subtopic) => subtopic.content.trim().length > 0).length);
const isEmpty = computed(() => writtenCount.value === 0);

/* Skipping the questions leaves every section blank, and telling that user their
   answers wrote the filled-in parts describes a screen they are not looking at. */
const introCopy = computed(() => (isEmpty.value ? t("onboarding.calendarFirst.profilePublishStep.intro.empty") : t("onboarding.calendarFirst.profilePublishStep.intro.written")));

const publishBody = computed(() => {
    const access = publishAccess.value;
    if (access === null) {
        return "";
    }
    return t("onboarding.calendarFirst.profilePublishStep.choice.publish", { audience: access });
});

const sharerFirstNames = computed(() =>
    profileSharerNames
        .slice(0, MAX_NAMED_SHARERS)
        .map((name) => firstNameOf(name))
        .join(", "),
);
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .eyebrow {
        @apply details tracking-wide text-ink-muted uppercase;
    }
    .rewritten {
        @apply body-small mb-6 flex items-start gap-2 text-ink-muted;
    }
    /* No margin of its own: the header above already ends on one, and the two
       stacked to 52px between the subtitle and the first section. */
    .document {
        @apply w-full;
    }
    .private-only {
        @apply body-small mt-6 text-ink-muted;
    }
    .choice-copy {
        @apply body-small mt-6 text-ink;
    }
}
</style>
